import { useState, useMemo } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { MASTER_MODULES, MASTER_GROUPS } from "@/lib/masterConfig";
import {
  LayoutDashboard, Users, ShieldCheck, Building2, ScrollText, Radio,
  GitBranch, Filter, Calendar, Folder, Bell, LogOut, ChevronRight, ChevronDown,
  Building, Layers, BedDouble, DoorOpen, IdCard, Sparkles, Tag, Boxes, Package, Truck,
  Wallet, Percent, BadgeDollarSign, Shirt, QrCode, Waves, Database, Hotel, LogIn,
  BookOpen, Landmark, FileCode2, CalendarClock, FileText, Download, Gauge, Wrench,
  UtensilsCrossed, Banknote, Radar, ChefHat, ClipboardList, Trash2, Monitor,
  BellRing, UserRound, HandPlatter, BarChart3, UserPlus, ChevronLeft, Droplets,
} from "lucide-react";
import { HocpMark } from "@/components/BrandLogo";
import HotelSwitcher from "@/components/HotelSwitcher";
import { useHotelServices } from "@/hooks/useHotelServices";
import { TooltipProvider } from "@/components/ui/tooltip";
import { SideNavItem, SideNavButton, NavSeparator, withTooltip } from "./SideNavItem";

const ICONS = {
  Building2, Building, Layers, BedDouble, DoorOpen, Users, IdCard, Sparkles, Tag,
  Boxes, Package, Truck, Wallet, Percent, BadgeDollarSign, Shirt, QrCode, Waves, Database,
  BookOpen, Landmark, FileCode2, CalendarClock,
};

// Each item may declare either `perm` (single string) OR `perms` (array — any match wins).
// Codes reflect the actual `require_permission()` codes the backend checks — which are
// the same codes the Visual Access Manager toggles write. Legacy codes are kept as
// alternates for backwards compat with old templates still in the DB.
const FOUNDATION_GROUPS = [
  { label: "Overview", items: [
    { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard", perm: "dashboard.view" },
    { to: "/reports", label: "Reports", icon: BarChart3, testid: "nav-reports", perm: "reports.view" },
    { to: "/operations", label: "AI Operations Center", icon: Radar, testid: "nav-operations", perm: "ops.hub.view" },
    { to: "/operations-hub", label: "Operations Hub", icon: BellRing, testid: "nav-ops-hub", perm: "ops.hub.view" },
    { to: "/workflow", label: "Workflow & Approvals", icon: GitBranch, testid: "nav-workflow", perm: "workflow.view" },
    { to: "/guests", label: "Guest Experience", icon: UserRound, testid: "nav-guests", perms: ["guests.read", "guest.view"] },
    { to: "/dashboard/executive", label: "Executive", icon: Sparkles, testid: "nav-executive", perm: "dashboard.executive.view" },
  ]},
  { label: "Property Management", items: [
    { to: "/fo", label: "Front Desk", icon: LayoutDashboard, testid: "nav-fo", perms: ["front_office.dashboard.view", "fo.dashboard.view"] },
    { to: "/fo/shift", label: "My Shift", icon: Banknote, testid: "nav-shift", perms: ["front_office.shift.operate.own", "front_office.dashboard.view", "fo.dashboard.view"] },
    { to: "/inventory", label: "Room Inventory", icon: BedDouble, testid: "nav-inventory", perms: ["inventory.read", "pms.reservations.view"] },
    { to: "/pms/reservations", label: "Reservations", icon: Calendar, testid: "nav-pms-reservations", perms: ["reservations.view", "pms.reservations.view"] },
    { to: "/pms/arrivals", label: "Arrivals", icon: LogIn, testid: "nav-pms-arrivals", perms: ["stays.checkin", "front_office.checkin.perform", "pms.checkin"] },
    { to: "/pms/departures", label: "Departures", icon: LogOut, testid: "nav-pms-departures", perms: ["stays.checkout_request", "front_office.checkout.perform", "pms.checkout"] },
    { to: "/checkout/queue", label: "Checkout Queue", icon: LogOut, testid: "nav-checkout-queue", perms: ["front_office.checkout.perform", "stays.checkout_request", "pms.checkout"] },
    { to: "/pms/guests", label: "Guests", icon: Users, testid: "nav-pms-guests", perms: ["guests.read", "guest.view"] },
    { to: "/quotations", label: "Quotations", icon: FileText, testid: "nav-quotations", perms: ["quotations.view"] },
    { to: "/pms/stays", label: "Stays", icon: Hotel, testid: "nav-pms-stays", perms: ["stays.view", "reservations.view", "pms.reservations.view"] },
  ]},
  { label: "Finance", items: [
    { to: "/finance-os", label: "Revenue & Finance OS", icon: Banknote, testid: "nav-finance-os", perm: "fin.folios.view" },
    { to: "/finance/folios", label: "Folios", icon: Wallet, testid: "nav-finance-folios", superOnly: true },
    { to: "/finance/payments", label: "Payments", icon: BadgeDollarSign, testid: "nav-finance-payments", superOnly: true },
  ]},
  { label: "Food & Beverage", items: [
    { to: "/fnb", label: "F&B OS", icon: UtensilsCrossed, testid: "nav-fnb-os", perm: "fnb.order.view",
      services: ["restaurant", "bar", "pool", "banquets", "coffee_shop", "room_service", "mini_bar"] },
    { to: "/fnb/pos", label: "Restaurant POS", icon: UtensilsCrossed, testid: "nav-fnb-pos", perm: "fnb.order.create", service: "restaurant" },
    { to: "/fnb/breakfast", label: "Breakfast Register", icon: UtensilsCrossed, testid: "nav-fnb-breakfast", perm: "fnb.order.view", service: "restaurant" },
    { to: "/fnb/bar", label: "Bar Display", icon: UtensilsCrossed, testid: "nav-fnb-bar", perm: "fnb.bar.view", service: "bar" },
  ]},
  { label: "Kitchen Operations", items: [
    { to: "/kitchen",          label: "Kitchen OS",       icon: ChefHat,        testid: "nav-kops", perm: "kitchen.pantry.view", service: "kitchen" },
    { to: "/kitchen/expo",     label: "Expediter",        icon: HandPlatter,    testid: "nav-kops-expo", perm: "kitchen.kds.view", service: "kitchen" },
    { to: "/kitchen/pantry",   label: "Kitchen Pantry",   icon: Package,        testid: "nav-kops-pantry", perm: "kitchen.pantry.view", service: "kitchen" },
    { to: "/kitchen/requests", label: "Pantry Requests",  icon: ClipboardList,  testid: "nav-kops-requests", perm: "kitchen.request.create", service: "kitchen" },
    { to: "/kitchen/waste",    label: "Waste Management", icon: Trash2,         testid: "nav-kops-waste", perm: "kitchen.pantry.view", service: "kitchen" },
    { to: "/fnb/kds",          label: "Kitchen Display",  icon: Monitor,        testid: "nav-kops-kds", perm: "kitchen.kds.view", service: "kitchen" },
  ]},
  { label: "Housekeeping", items: [
    { to: "/housekeeping",                 label: "Housekeeping",     icon: Sparkles, testid: "nav-housekeeping", perm: "hk.tasks.view" },
    { to: "/housekeeping/mission-control", label: "Mission Control",  icon: Radar,    testid: "nav-hk-mc",        perm: "hk.tasks.view" },
    { to: "/housekeeping/me",              label: "My Rooms",         icon: BedDouble, testid: "nav-my-rooms",    perm: "hk.tasks.execute" },
    { to: "/housekeeping/consumption",     label: "Room Consumption", icon: Droplets, testid: "nav-hk-consumption", perm: "hk.tasks.view" },
    { to: "/housekeeping/inventory",          label: "HK Inventory",     icon: Boxes,         testid: "nav-hkinv",          perm: "hkinv.inventory.view", end: true },
    { to: "/housekeeping/inventory/requests", label: "Storage Requests", icon: ClipboardList, testid: "nav-hkinv-requests",  perm: "hkinv.inventory.view" },
    { to: "/housekeeping/inventory/usage",    label: "Usage Log",        icon: Layers,        testid: "nav-hkinv-usage",    perm: "hkinv.usage.create" },
  ]},
  { label: "Engineering & Maintenance", items: [
    { to: "/maintenance",       label: "Maintenance OS",  icon: Wrench,  testid: "nav-maintenance",       perm: "mnt.wo.view" },
    { to: "/engineering",       label: "Engineering",     icon: Wrench,  testid: "nav-engineering",       perm: "mnt.wo.view" },
    { to: "/engineering/me",    label: "My Work Orders",  icon: Wrench,  testid: "nav-my-work-orders",    perm: "mnt.wo.progress" },
  ]},
  { label: "Pool", items: [
    { to: "/master/costume-catalogue", label: "Costume Catalogue", icon: Shirt,   testid: "nav-pool-catalogue", perms: ["master.view", "master.edit"], service: "pool" },
    { to: "/master/costume-assets",    label: "Costume Assets",    icon: QrCode,  testid: "nav-pool-assets",    perms: ["master.view", "master.edit"], service: "pool" },
  ]},
  { label: "Workforce", items: [
    { to: "/team", label: "Team Members", icon: UserPlus, testid: "nav-team", perms: ["team_members.view", "wf.employees.view"] },
    { to: "/wf", label: "Workforce OS", icon: ShieldCheck, testid: "nav-wf", perm: "wf.employees.view" },
    { to: "/wf/system-access", label: "System Access", icon: ShieldCheck, testid: "nav-wf-sysaccess", perm: "wf.employees.view" },
    { to: "/roster", label: "Roster", icon: Calendar, testid: "nav-roster", perm: "attendance.view" },
    { to: "/attendance", label: "Attendance", icon: UserRound, testid: "nav-attendance", perm: "attendance.view" },
    { to: "/wf/matrix", label: "Permission Matrix", icon: ShieldCheck, testid: "nav-wf-matrix", perm: "wf.access.manage" },
    { to: "/supply", label: "Supply OS", icon: Package, testid: "nav-supply", perm: "supply.view" },
    { to: "/supply/digital-twin", label: "Inventory Digital Twin", icon: Radar, testid: "nav-supply-twin", perm: "supply.view" },
    { to: "/security", label: "Security", icon: ShieldCheck, testid: "nav-security", superOnly: true },
    { to: "/admin/readiness-catalogs", label: "Readiness Catalogs", icon: Gauge, testid: "nav-readiness-catalogs", superOnly: true },
  ]},
  { label: "Automation", items: [
    { to: "/hotel-settings/services", label: "Hotel Services", icon: ShieldCheck, testid: "nav-hotel-services", adminOnly: true },
    { to: "/rules", label: "Rule Engine", icon: Filter, testid: "nav-rules", adminOnly: true },
    { to: "/scheduler", label: "Scheduler", icon: Calendar, testid: "nav-scheduler", adminOnly: true },
  ]},
  { label: "Observability", items: [
    { to: "/audit", label: "Audit Log", icon: ScrollText, testid: "nav-audit", adminOnly: true },
    { to: "/events", label: "Events", icon: Radio, testid: "nav-events", adminOnly: true },
  ]},
  { label: "Content", items: [
    { to: "/notifications", label: "Notifications", icon: Bell, testid: "nav-notifications" },
  ]},
];

export default function Sidebar({ collapsed = false, mobile = false, onClose, onToggle }) {
  const { user, logout } = useAuth();
  const { enabled: enabledServices } = useHotelServices();
  const nav = useNavigate();
  const loc = useLocation();

  // Paths that are a strict prefix of a sibling need `end` so they don't stay active on child routes.
  const parentPaths = useMemo(() => {
    const all = FOUNDATION_GROUPS.flatMap((g) => g.items.map((i) => i.to));
    return new Set(all.filter((p) => all.some((q) => q !== p && q.startsWith(p + "/"))));
  }, []);

  const [openGroups, setOpenGroups] = useState(() => {
    const init = {};
    MASTER_GROUPS.forEach(g => { init[g.label] = false; });
    const cur = MASTER_MODULES.find(m => loc.pathname.includes(`/master/${m.key}`));
    if (cur) init[cur.group] = true;
    return init;
  });

  const masterByGroup = useMemo(() => {
    const map = {};
    MASTER_MODULES.forEach(m => { (map[m.group] = map[m.group] || []).push(m); });
    return map;
  }, []);

  // WF-2 · capability-based sidebar filtering (super admins see everything).
  const isSA = user?.role === "super_admin";
  const perms = new Set(user?.permissions || []);
  const hasWildcard = perms.has("*");
  const isHotelAdmin = isSA || hasWildcard || perms.has("wf.*") || perms.has("wf.security.manage");
  const canSee = (item) => {
    // HOTEL-SVC-1 · service gate runs first, fail-closed.
    if (item.service || item.services) {
      const wantSvcs = item.services || [item.service];
      if (!wantSvcs.some((s) => enabledServices.has(s))) return false;
    }
    if (item.superOnly) return isSA;
    if (item.adminOnly) return isHotelAdmin;
    if (isSA || hasWildcard) return true;
    const wanted = [];
    if (item.perm) wanted.push(item.perm);
    if (Array.isArray(item.perms)) wanted.push(...item.perms);
    if (!wanted.length) return true;
    for (const w of wanted) {
      if (perms.has(w)) return true;
      for (const p of perms) {
        if (p.endsWith(".*") && w.startsWith(p.slice(0, -1))) return true;
        if (p.endsWith("*") && w.startsWith(p.slice(0, -1))) return true;
      }
    }
    return false;
  };
  const showMaster = isSA || (user?.permissions || []).some(p => p === "*" || p.startsWith("master."));
  const expand = () => { if (collapsed) onToggle?.(); };

  return (
    <TooltipProvider>
      {/* Brand + collapse toggle */}
      <div className={`h-16 flex items-center border-b border-hocp-deepTeal shrink-0 ${collapsed ? "justify-center px-0" : "px-4 gap-3"}`}>
        {collapsed ? (
          <button type="button" onClick={onToggle} className="w-8 h-8 rounded-lg bg-hocp-mint text-hocp-midnight font-display font-bold text-[16px] flex items-center justify-center" data-testid="sidebar-brand-mini" aria-label="Expand sidebar">H</button>
        ) : (
          <>
            <div className="w-9 h-9 rounded-lg bg-white flex items-center justify-center shadow-sm shrink-0"><HocpMark size={30} /></div>
            <div className="font-display font-bold tracking-tight text-[18px] text-white leading-none flex items-center gap-1.5" data-testid="sidebar-brand">HOCP<span className="w-1.5 h-1.5 rounded-full bg-[#F3B940] inline-block" /></div>
            {!mobile && (
              <button type="button" onClick={onToggle} className="ml-auto w-9 h-9 flex items-center justify-center rounded-lg text-hocp-softIce hover:bg-hocp-deepTeal" data-testid="sidebar-collapse" aria-label="Collapse sidebar">
                <ChevronLeft size={20} strokeWidth={1.5} />
              </button>
            )}
          </>
        )}
      </div>
      {collapsed && (
        <button type="button" onClick={onToggle} className="mx-auto mt-2 w-9 h-9 flex items-center justify-center rounded-lg text-hocp-softIce hover:bg-hocp-deepTeal" data-testid="sidebar-expand" aria-label="Expand sidebar">
          <ChevronRight size={20} strokeWidth={1.5} />
        </button>
      )}

      {/* Hotel Switcher (ADR-WF-1 v2.1 §25) */}
      {collapsed ? (
        <div className="px-2 pt-2">{withTooltip(true, "Switch hotel", <button type="button" onClick={expand} className="w-full h-11 flex items-center justify-center rounded-lg text-hocp-softIce/70 hover:bg-hocp-deepTeal hover:text-white" data-testid="sidebar-hotel-mini" aria-label="Switch hotel"><Building2 size={22} strokeWidth={1.5} /></button>)}</div>
      ) : (
        <div className="px-3 pt-3"><HotelSwitcher /></div>
      )}

      {/* Nav */}
      <nav className={`flex-1 overflow-y-auto overflow-x-hidden py-4 ${collapsed ? "px-1 space-y-1" : "px-2 space-y-5"}`} data-testid="sidebar-nav">
        {FOUNDATION_GROUPS.map((g) => {
          const visibleItems = g.items.filter(canSee);
          if (!visibleItems.length) return null;
          return (
            <div key={g.label} className="space-y-0.5">
              {collapsed ? <NavSeparator collapsed /> : (
                <div className="px-3 text-[10px] font-bold uppercase tracking-[0.14em] mb-1.5 text-hocp-softIce/45">{g.label}</div>
              )}
              {visibleItems.map((it) => (
                <SideNavItem key={it.to} to={it.to} label={it.label} icon={it.icon} testid={it.testid} collapsed={collapsed} onNavigate={onClose}
                  end={it.to === "/" || it.to === "/fo" || it.to === "/dashboard" || parentPaths.has(it.to)} />
              ))}
            </div>
          );
        })}

        {/* Master Data — Super Admins + Hotel Admin roles */}
        {showMaster && (
          <div className="space-y-0.5">
            {collapsed ? (
              <>
                <NavSeparator collapsed />
                <SideNavButton label="Master Data" icon={Database} collapsed onClick={expand} testid="master-data-mini" active={loc.pathname.startsWith("/master/")} />
              </>
            ) : (
              <>
                <div className="px-3 text-[10px] font-bold uppercase tracking-[0.14em] mb-1.5 text-hocp-softIce/45 flex items-center gap-1.5">
                  <Database size={12} strokeWidth={2} /> Master Data
                </div>
                {MASTER_GROUPS.map((grp) => {
                  const items = masterByGroup[grp.label] || [];
                  if (!items.length) return null;
                  if (grp.service && !enabledServices.has(grp.service)) return null;
                  const isOpen = openGroups[grp.label];
                  const GrpIcon = ICONS[grp.icon] || Database;
                  return (
                    <div key={grp.label}>
                      <SideNavButton label={grp.label} icon={GrpIcon} size="sm" onClick={() => setOpenGroups({ ...openGroups, [grp.label]: !isOpen })}
                        testid={`master-group-${grp.label.toLowerCase()}`}
                        trailing={<ChevronDown size={14} strokeWidth={2} className={`transition-transform ${isOpen ? "" : "-rotate-90"}`} />} />
                      {isOpen && (
                        <div className="pl-2 mt-0.5 space-y-0.5 border-l border-hocp-deepTeal ml-5">
                          {items.map((m) => (
                            <SideNavItem key={m.key} to={`/master/${m.key}`} label={m.label} icon={ICONS[m.icon] || Database} size="sm" testid={`nav-master-${m.key}`} onNavigate={onClose} />
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </>
            )}
          </div>
        )}
      </nav>

      {/* Footer */}
      <div className={`border-t border-hocp-deepTeal shrink-0 space-y-0.5 ${collapsed ? "p-1" : "p-2"}`}>
        {withTooltip(collapsed, "Platform Guide", (
          <a href={`${process.env.REACT_APP_BACKEND_URL}/api/guides/platform-v2-1`} target="_blank" rel="noopener noreferrer" data-testid="download-guide" aria-label="Platform Guide"
            className={`flex items-center gap-3 rounded-lg min-h-[44px] text-[13px] text-hocp-softIce/70 hover:bg-hocp-deepTeal hover:text-white transition-colors ${collapsed ? "justify-center" : "px-3"}`}>
            <FileText size={20} strokeWidth={1.5} />
            {!collapsed && <><span className="flex-1 truncate">Platform Guide</span><Download size={16} strokeWidth={1.5} className="opacity-60" /></>}
          </a>
        ))}
        {withTooltip(collapsed, "Sign out", (
          <button type="button" onClick={async () => { await logout(); nav("/login"); }} data-testid="logout-button" aria-label="Sign out"
            className={`w-full flex items-center gap-3 rounded-lg min-h-[44px] text-[13px] text-hocp-softIce/70 hover:bg-hocp-deepTeal hover:text-white transition-colors ${collapsed ? "justify-center" : "px-3"}`}>
            <LogOut size={20} strokeWidth={1.5} />
            {!collapsed && <span>Sign out</span>}
          </button>
        ))}
      </div>
    </TooltipProvider>
  );
}
